import { forwardRef, ReactNode, useMemo, useState } from 'react';
import { DataGrid, Menu } from 'devextreme-react';
import { Column, FilterRow, Paging, Scrolling, Selection } from 'devextreme-react/data-grid';
import type { ItemClickEvent, Item as MenuItem } from 'devextreme/ui/menu';
import { uaFilterRowText } from 'app-constants';
import { dsServicesNoms } from 'datasources';
import { useSearchDatagridHandler } from 'hooks';
import { SearchDropdown } from 'components/search-dropdown/search-dropdown';

interface NomItem {
  ref: string;
  name: string;
}

interface NomsMenuItem extends MenuItem {
  id?: string;
}

interface NomsDatagridProps {
  searchField?: string;
  onSelectionChanged?: (row: NomItem) => void;
  dataSource?: object[] | object | null;
  value?: NomItem | null;
}

interface NomsSearchProps {
  nom?: NomItem | null;
  onSelect: (data: NomItem) => void;
  dataSource?: object[] | object | null;
  readOnly?: boolean;
  stylingMode?: string;
  validator?: ReactNode;
  width?: string | number;
  [key: string]: unknown;
}

const NomsDatagrid = forwardRef<unknown, NomsDatagridProps>(
  ({ searchField = 'name', onSelectionChanged, dataSource }, ref) => {
    const { gridRef, focusElementRef, dataReady, onCurrentRowChange }
      = useSearchDatagridHandler(ref, searchField, onSelectionChanged);
    const [ currentRowData, setCurrentRowData ] = useState<NomItem | null>(null);

    const clickMenu = (e: ItemClickEvent) => {
      const item = e.itemData as NomsMenuItem;
      if (item?.id === 'select' && currentRowData) onSelectionChanged?.(currentRowData);
    };

    const menuItems: NomsMenuItem[] = [
      {
        text: 'Вибрати',
        id: 'select',
        icon: 'check',
        visible: !!onSelectionChanged,
        disabled: !currentRowData,
      },
    ];

    const grid = useMemo(() => (
      <DataGrid
        ref={gridRef}
        remoteOperations={true}
        showBorders
        dataSource={dataReady ? dataSource : []}
        hoverStateEnabled={true}
        onRowDblClick={(e) => onSelectionChanged?.(e.data as NomItem)}
        onSelectionChanged={(e) => {
          if (e.selectedRowsData.length > 0) {
            setCurrentRowData(e.selectedRowsData[0] as NomItem);
            onCurrentRowChange(e.selectedRowsData[0] as NomItem);
          }
        }}
        height='90%'
        width='100%'
      >
        <Selection mode='single'/>
        <Scrolling mode='virtual' rowRenderingMode='virtual' />
        <Paging enabled={true} pageSize={100} />
        <FilterRow {...uaFilterRowText}/>
        <Column
          dataField='name'
          caption='Назва'
          dataType='string'
          allowSorting={false}
          alignment='left'
          width={120}
        />
        <Column
          dataField='name_full'
          caption='Повна'
          dataType='string'
          allowSorting={false}
          alignment='left'
          width={120}
        />
      </DataGrid>
    ), [ dataReady, dataSource, gridRef, onCurrentRowChange, onSelectionChanged ]);

    return (
      <div
        ref={focusElementRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
      >
        <Menu
          onItemClick={clickMenu}
          activeStateEnabled={false}
          dataSource={menuItems}
        />
        {grid}
      </div>
    );
  }
);

NomsDatagrid.displayName = 'NomsDatagrid';

export const NomsSearch = (props: NomsSearchProps) => (
  <SearchDropdown
    {...props}
    value={props.nom || { ref: '', name: '' }}
    text={props.nom?.name || ''}
    onSelect={props.onSelect}
    minSearchLength={1}
    searchTimeout={500}
    placeholder='Номенклатуры...'
    saveSearchValueOnLeave={true}
    calcSearchValue={() => ''}
    width='100%'
    dropdownWidth={800}
    dropdownHeight={400}
  >
    <NomsDatagrid dataSource={props.dataSource || dsServicesNoms} />
  </SearchDropdown>
);
